import React, { useState } from 'react';
import CheckinWeekStrip from './CheckinWeekStrip';
import { actionTargetLabel, goalsActionTargetKeys, groupActionsByTarget } from '../../utils/actionTargets';
import '../../styles/adminApproval.css'; // 行動記録 strip + CheckinSheet styles (were only loaded via 管理画面)

/**
 * 📋 毎日の行動プラン — plan rows + 行動記録（過去7日）. Shown on ホーム under the graph
 * (moved from 目標, 2026-09-30, docs/03_TASKS/2026-09-30_行動プランをホームへ移動.md).
 * `actions` = D.actions (display rows), `goals` = useGoalsConfig doc.
 */
export default function ActionPlanCard({ actions = [], goals, store, stylist, canEdit, onEdit, checkinVersion = 0 }) {
  /** Tap/expand which plan row shows いつ/何を/どのくらい */
  const [expandedActionId, setExpandedActionId] = useState(null);
  // Per 指標 only when every action is tagged with a current 指標; else the flat list as before
  const groups = groupActionsByTarget(actions, goalsActionTargetKeys(goals)) || [{ key: null, actions }];
  const renderRow = (a) => {
    const hasDetail = Boolean(a.when || a.what || a.howMuch);
    const open = expandedActionId === a.id;
    return (
      <div
        className={`plan-row${open ? ' is-open' : ''}${hasDetail ? ' is-expandable' : ''}`}
        key={a.id}
      >
        <button
          type="button"
          className="plan-row-main"
          onClick={() => {
            if (!hasDetail) return;
            setExpandedActionId(open ? null : a.id);
          }}
          aria-expanded={hasDetail ? open : undefined}
          disabled={!hasDetail}
        >
          <span className="plan-num">{a.id}</span>
          <div className="plan-body">
            <div className="plan-name">{a.name}</div>
            <div className="plan-cadence">
              {a.cadence}
              {hasDetail ? (open ? ' · 閉じる' : ' · 詳細') : ''}
            </div>
          </div>
          <span className={`plan-rate ${a.tone}`}>{a.rate}</span>
        </button>
        {open && hasDetail && (
          <div className="plan-detail">
            {a.when && (
              <div className="plan-detail-row">
                <span className="plan-detail-label">いつ</span>
                <span className="plan-detail-text">{a.when}</span>
              </div>
            )}
            {a.what && (
              <div className="plan-detail-row">
                <span className="plan-detail-label">何を</span>
                <span className="plan-detail-text">{a.what}</span>
              </div>
            )}
            {a.howMuch && (
              <div className="plan-detail-row">
                <span className="plan-detail-label">どのくらい</span>
                <span className="plan-detail-text">{a.howMuch}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="plan">
      <div className="plan-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>
          📋 毎日の行動プラン
          {goals?.approvalStatus === 'pending' && (
            <span className="plan-badge plan-badge--pending">確認中</span>
          )}
          {goals?.approvalStatus === 'approved' && (
            <span className="plan-badge plan-badge--approved">確定</span>
          )}
        </span>
        <button type="button" className="set-group-edit" onClick={onEdit}>
          編集
        </button>
      </div>
      {goals?.approvalStatus === 'pending' && (
        <div className="plan-pending">
          <div className="plan-pending-note">マネージャーが確認中です。確定すると毎日の行動プランになります。</div>
          {(goals.pendingActions || []).map((a, i) => (
            <div className="plan-row plan-row--pending" key={a.actionId || i}>
              <div className="plan-row-main" style={{ cursor: 'default' }}>
                <span className="plan-num">{i + 1}</span>
                <div className="plan-body">
                  <div className="plan-name">{a.name}</div>
                  <div className="plan-cadence">{a.cadence || '—'}</div>
                </div>
                <span className="plan-badge plan-badge--pending">確認中</span>
              </div>
            </div>
          ))}
          {actions.length > 0 && <div className="plan-pending-sub">いまの行動プラン（確定済み）</div>}
        </div>
      )}
      {actions.length === 0 ? (
        goals?.approvalStatus === 'pending' ? null : (
          <div style={{ padding: '12px 4px', fontSize: '13px', color: 'var(--muted)' }}>行動プランは未設定です</div>
        )
      ) : (
        groups.map((g) => (
          <React.Fragment key={g.key || 'all'}>
            {g.key && <div className="plan-kpi">{actionTargetLabel(g.key, goals?.subKgiKey)}</div>}
            {g.actions.map(renderRow)}
          </React.Fragment>
        ))
      )}
      {(goals?.actions || []).length > 0 && (
        <CheckinWeekStrip store={store} stylist={stylist} actions={goals.actions} canEdit={canEdit} refreshKey={checkinVersion} />
      )}
    </div>
  );
}
